import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { api, ApiHttpError } from "./lib/api";
import { track } from "./analytics";
import { Button, cx } from "./control/ui/primitives";

/**
 * Age gate shown on first load. Blocks the whole app (control panel + displays)
 * behind a single yes/no confirmation.
 *
 * It collected a date of birth through three selects. That looked like verification and
 * was not: nothing checked the date against anything, so the whole apparatus — a hundred
 * years of options, the rejection of 31 February, the day-before-your-birthday edge case —
 * measured only whether a visitor was willing to type a year. Asking directly is the same
 * question with the pretence removed, and three fewer things to get through on a phone.
 *
 * The pass is server-side and unchanged: /api/age/verify sets an httpOnly cookie and media
 * URLs are signed against that cookie's subject. This used to be a localStorage flag,
 * which meant the gate stopped nothing — the media routes had no auth at all, so any HTTP
 * client walked straight past it. The flag is still written, but only as a hint that lets
 * a returning visitor skip a network round-trip; the cookie is the authority and its
 * absence re-prompts.
 *
 * So the thing that actually gates bytes did not move. Only the question did.
 */

const MIN_AGE = 18;
const STORAGE_KEY = "gp:age-verified";
const STORAGE_VERSION = 1;
/** Where "Leave" sends anyone who declines or is under age. */
const EXIT_URL = "https://www.google.com";

/**
 * Kept only as a local breadcrumb. It is deliberately NOT read back to decide
 * access: the httpOnly cookie is the authority, and trusting this flag is what
 * made the old gate ornamental. If the cookie has expired, the server says so
 * and the form reappears no matter what this says.
 */
function writeVerified(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: STORAGE_VERSION, ts: Date.now() }));
  } catch {
    /* private mode / disabled storage — gate just re-prompts next load */
  }
}

interface GateCfg {
  enabled: boolean;
  minAge: number;
}

export function AgeGate({ children }: { children: ReactNode }) {
  const [cfg, setCfg] = useState<GateCfg | null>(null);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.age
      .status()
      .then(async (s) => {
        if (cancelled) return;
        if (s.verified) {
          setCfg({ enabled: s.required, minAge: s.minAge });
          setVerified(true);
          return;
        }
        // Gate switched off in admin settings, but media signatures are enforced
        // unconditionally — so a token is still required or every clip 403s.
        // Claim one silently rather than showing a form nobody asked for.
        if (!s.required) {
          try {
            await api.age.verify();
            if (!cancelled) setVerified(true);
          } catch {
            /* fall through to the gate rather than a blank app */
          }
        }
        if (!cancelled) setCfg({ enabled: s.required, minAge: s.minAge });
      })
      // Fail closed: if status can't be read, still show the default gate.
      .catch(() => !cancelled && setCfg({ enabled: true, minAge: MIN_AGE }));
    return () => {
      cancelled = true;
    };
  }, []);

  if (verified) return <>{children}</>;
  if (!cfg) {
    // Brief, gate-shaped loader so we never flash the form before we know config.
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-ink)]">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
      </div>
    );
  }
  return (
    <AgeGateScreen
      minAge={cfg.minAge}
      onPass={() => {
        writeVerified();
        setVerified(true);
      }}
    />
  );
}

function AgeGateScreen({ minAge, onPass }: { minAge: number; onPass: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function confirm() {
    setSubmitting(true);
    try {
      await api.age.verify();
      setError(null);
      // The first hard gate, so the pass/reject split is the top of every funnel below
      // it. Nothing about the person is attached — only that the server said yes.
      track("age_gate_passed");
      onPass();
    } catch (err) {
      // 403 is the server refusing; anything else is a transport failure and must not be
      // reported as a refusal. Telling somebody they were rejected because the server was
      // briefly unreachable sends them away for good.
      if (err instanceof ApiHttpError && err.status === 403) {
        setError(null);
        setDenied(true);
        track("age_gate_rejected");
      } else {
        setError("Couldn't reach the server. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  /** Declining needs no round trip — there is nothing to record about someone leaving. */
  function leave() {
    track("age_gate_rejected");
    window.location.href = EXIT_URL;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-ink)] p-4">
      {/* soft accent glow behind the card */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60rem 40rem at 50% -10%, color-mix(in srgb, var(--color-accent) 22%, transparent), transparent 70%)",
        }}
      />
      <div className="relative w-full max-w-md rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="text-xs font-semibold tracking-[0.2em] text-[var(--color-accent-soft)]">
            goonlords
          </div>
          <h1 className="mt-2 text-2xl font-semibold text-zinc-100">
            {denied ? "Access denied" : `Are you ${minAge} or older?`}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            {denied
              ? `You must be at least ${minAge} years old to view this content.`
              : "This site contains adult content."}
          </p>
        </div>

        {denied ? (
          <Button
            variant="primary"
            className="w-full"
            onClick={() => (window.location.href = EXIT_URL)}
          >
            Leave
          </Button>
        ) : (
          <>
            <div
              className={cx(
                "mb-3 min-h-[1.25rem] text-center text-xs",
                error ? "text-red-400" : "text-transparent",
              )}
              aria-live="polite"
            >
              {error ?? "placeholder"}
            </div>

            {/* Yes first and visually primary. The two answers are not equal weight —
                almost everybody here is answering yes, and burying that behind a neutral
                pair of buttons makes the common case read like a decision. */}
            <div className="flex flex-col gap-2">
              <Button variant="primary" className="w-full" onClick={confirm} disabled={submitting}>
                {submitting ? "One moment…" : `Yes, I'm ${minAge} or older`}
              </Button>
              <Button variant="ghost" className="w-full" onClick={leave} disabled={submitting}>
                No — take me out
              </Button>
            </div>

            {/* The attestation itself, on screen at the moment it is made rather than in a
                policy page nobody opens. This sentence is the whole of what "yes" means. */}
            <p className="mt-5 text-center text-[11px] leading-relaxed text-zinc-500">
              By continuing you confirm you are of legal age to view adult material in your
              jurisdiction.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

